export function getCurrencyString(num) {
  return new Intl.NumberFormat().format(num, {
    style: 'currencyWithGroupingSeparator',
  });
}

export function formatStrNumber(value) {
  // 숫자가 아닌 문자 제거
  const formattedValue = value.replace(/[^0-9]/g, '');
  const numberWithCommas = formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return numberWithCommas;
}

// 숫자 형식 가공 및 콤마 추가 함수
export function formatStrFloat(value) {
  const stringValue = String(value); // 문자열로 변환

  const parts = stringValue.split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  let decimalPart = parts[1] ? `.${parts[1]}` : '';
  if (stringValue.charAt(stringValue.length - 1) === '.') {
    decimalPart = '.';
  }

  return `${integerPart}${decimalPart}`;
}

export function formatNumNumber(value) {
  // 숫자가 아니면 빈 문자열 반환
  if (value === undefined || value === null) {
    return '0';
  }
  // 숫자가 아닌 문자 제거
  const formattedValue = value.toString().replace(/[^0-9]/g, '');
  const numberWithCommas = formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return numberWithCommas;
}

export function formatNumber(value) {
  // 숫자가 아니면 빈 문자열 반환
  if (value === undefined || value === null) {
    return '0';
  }
  // 숫자가 아닌 문자 제거
  const formattedValue = value.toString().replace(/[^0-9]/g, '');

  return formattedValue;
}

export function formatFloatNumber(value, totalDigits) {
  if (value === undefined) {
    return '0';
  }
  const formattedValue = value.replace(/[^\d.]/g, '');
  const parts = formattedValue.split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  let decimalPart = '';
  if (parts[1] !== undefined) {
    // 소수점 이하 totalDigits만큼만 유지
    decimalPart = `.${parts[1].slice(0, totalDigits)}`;
  }
  return `${integerPart}${decimalPart}`;
}

export function delComString(str) {
  const isNumber = /^\d+$/.test(str);
  if (isNumber) {
    return str;
  } else {
    return str.replaceAll(',', '');
  }
}

/** Nest/axios 응답 body에서 사람이 읽을 메시지 추출 */
function extractApiMessage(data) {
  if (!data || typeof data !== 'object') return '';
  const m = data.message;
  if (typeof m === 'string' && m.trim()) return m.trim();
  if (Array.isArray(m) && m.length) {
    const parts = m
      .map(item => {
        if (typeof item === 'string') return item;
        if (item?.constraints) return Object.values(item.constraints).join(' ');
        return item?.message || '';
      })
      .filter(Boolean);
    if (parts.length) return parts.join('\n');
  }
  if (typeof data.error === 'string' && data.error.trim())
    return data.error.trim();
  return '';
}

/**
 * API 에러 응답에 따른 상황별 메시지
 * @param {object} response - axios error.response (또는 fetchData가 반환한 동일 형태)
 * @returns {string} 사용자에게 보여줄 에러 메시지
 */
export function getErrorMessage(response) {
  const fallback =
    '요청 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
  if (!response) return fallback;

  const statusCode =
    response?.status ?? response?.statusCode ?? response?.data?.statusCode;
  const serverMsg = extractApiMessage(response.data);
  if (serverMsg) return serverMsg;

  if (statusCode === 400)
    return '요청이 올바르지 않습니다. 입력 내용을 확인해주세요.';
  if (statusCode === 401) return '로그인이 필요합니다. 다시 로그인해주세요.';
  if (statusCode === 403) return '접근 권한이 없습니다.';
  if (statusCode === 404) return '요청한 정보를 찾을 수 없습니다.';
  if (statusCode === 409) return '이미 존재하거나 충돌하는 데이터입니다.';
  if (statusCode === 429)
    return '요청이 너무 많습니다. 잠시 후 다시 시도해주세요.';
  if (statusCode >= 500)
    return '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
  if (statusCode >= 400) return fallback;
  return fallback;
}

/**
 * fetchData 반환값이 성공(2xx)인지 확인
 */
export function isSuccessResponse(ret) {
  if (!ret) return false;
  const status = ret.status ?? ret?.data?.statusCode;
  return status >= 200 && status < 300;
}

/** ISO datetime을 서울 시간 기준 YYYY-MM-DD로 변환 (UTC slice 방지) */
export function toSeoulDateString(value) {
  if (!value) return '';
  try {
    const str = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const d = new Date(str);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' });
  } catch (_) {
    return '';
  }
}

export function getFormattedDateTime(rawDateTime) {
  // 현재 날짜 및 시간 가져오기
  const currentDate = new Date(rawDateTime);

  // 년, 월, 일 가져오기
  const year = currentDate.getFullYear();
  const month = String(currentDate.getMonth() + 1).padStart(2, '0'); // 월은 0부터 시작하므로 +1, 두 자리로 만들기
  const day = String(currentDate.getDate()).padStart(2, '0'); // 두 자리로 만들기

  // 시, 분 가져오기
  const hours = String(currentDate.getHours()).padStart(2, '0'); // 두 자리로 만들기
  const minutes = String(currentDate.getMinutes()).padStart(2, '0'); // 두 자리로 만들기

  // 포맷된 문자열 생성 (YYYY-MM-DD HH:mm)
  const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}`;

  return formattedDateTime;
}
